import {
  archivedLeadListResponseSchema,
  deleteLeadResponseSchema,
  leadWriteResponseSchema,
  type ArchiveLeadBody,
  type ArchivedLeadListQuery,
  type ArchivedLeadListResponse,
  type ArchivedLeadRow,
  type DeleteLeadBody,
  type LeadListResponse,
} from "@gioia365/contracts";
import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { z } from "zod";
import { ApiRequestError, apiRequest } from "../../api/http";
import { tabKey, type TabContext, type TabKey } from "../../session/tab-context";
import { refreshSmartListMenu } from "../../smart-lists/menu-refresh";

// Le letture e le scritture dell'archivio dei Lead (piano M3 Task 12; rotte del Task 11, D9): «Archiviati (N)» con le
// sue pagine, «Archivia» dalla scheda, «Ripristina» ed «Elimina per sempre» dalla finestra degli archiviati. Le chiavi
// stanno sotto `["scheda", tab, "leads", "archived"]`, così un cambio dei permessi o di «Chi vede i Lead», che rilegge
// tutte le letture dei Lead, rilegge anche queste; dopo la parte fissa viene la vista (la Vista Commerciale osservata,
// oppure `null`), perché gli archiviati si leggono nel perimetro di chi guarda.

/** Le righe di una pagina della finestra degli archiviati («Mostra altri»). */
export const ARCHIVED_PAGE_SIZE = 50;

export const archivedKeys = {
  all: (tab: TabKey) => ["scheda", tab, "leads", "archived"] as const,
  page: (tab: TabKey, view: string | null, query: ArchivedLeadListQuery) => ["scheda", tab, "leads", "archived", view, "page", query] as const,
  pages: (tab: TabKey, view: string | null, q: string) => ["scheda", tab, "leads", "archived", view, "pages", q] as const,
};

function archivedPath(query: ArchivedLeadListQuery): string {
  const params = new URLSearchParams();
  if (query.q !== undefined && query.q !== "") params.set("q", query.q);
  params.set("offset", String(query.offset));
  params.set("limit", String(query.limit));
  return `/api/leads/archived?${params.toString()}`;
}

/**
 * Una pagina degli archiviati (`GET /api/leads/archived`), dal più recente. Con `limit: 1` dà soltanto il totale:
 * «Archiviati (N)» sotto l'elenco.
 */
export function archivedLeadsQuery(tab: TabContext, query: ArchivedLeadListQuery, view: string | null) {
  return queryOptions({
    queryKey: archivedKeys.page(tabKey(tab), view, query),
    queryFn: ({ signal }) => apiRequest("GET", archivedPath(query), { schema: archivedLeadListResponseSchema, tab, signal }),
    // Come l'elenco: tornare sulla pagina entro mezzo minuto non lo rilegge; ogni scrittura dell'archivio sì.
    staleTime: 30_000,
  });
}

/** Le pagine della finestra degli archiviati per una ricerca (già ripulita, vuota senza ricerca): «Mostra altri» legge la successiva. */
export function archivedLeadsPagesQuery(tab: TabContext, q: string, view: string | null) {
  return infiniteQueryOptions({
    queryKey: archivedKeys.pages(tabKey(tab), view, q),
    queryFn: ({ signal, pageParam }) =>
      apiRequest("GET", archivedPath({ ...(q ? { q } : {}), offset: pageParam, limit: ARCHIVED_PAGE_SIZE }), { schema: archivedLeadListResponseSchema, tab, signal }),
    initialPageParam: 0,
    getNextPageParam: (last: ArchivedLeadListResponse) => (last.offset + last.rows.length < last.total && last.rows.length > 0 ? last.offset + last.limit : undefined),
  });
}

/** I conteggi accanto alle voci di «Filtri» (piano M3 Task 4): contano i Lead non archiviati. */
const filterOptionsPrefix = (tab: TabKey) => ["scheda", tab, "leads", "filter-options"] as const;

/**
 * Dopo un cambio dell'archivio: gli elenchi dei Lead (anche quelli delle Smart List, che stanno sotto la stessa chiave),
 * i conteggi accanto alle voci dei filtri (decisione P36), gli archiviati e i Lead per owner della pagina Team si
 * rileggono, e il menu delle Smart List con i suoi numeri e le sue novità, con la regola di `refreshSmartListMenu` (anche a
 * rail chiusa, mai due letture insieme). Le azioni multiple (Task 14) e «Pulizia dati» cambiano gli stessi numeri e lo
 * usano anche loro.
 */
export function refreshAfterArchiveChange(queryClient: QueryClient, tab: TabContext): void {
  const part = tabKey(tab);
  for (const queryKey of [["scheda", part, "leads", "list"], filterOptionsPrefix(part), archivedKeys.all(part), ["scheda", part, "lead-owners", "counts"]]) {
    void queryClient.invalidateQueries({ queryKey });
  }
  refreshSmartListMenu(queryClient, tab);
}

/** Le schede e le Timeline di questi Lead (`null`: di tutti), in qualunque vista: il filtro della cache. */
export function leadCardsFilter(tab: TabKey, leadIds: ReadonlySet<string> | null) {
  return {
    queryKey: ["scheda", tab, "leads"],
    predicate: (query: { queryKey: QueryKey }) =>
      (query.queryKey[3] === "card" || query.queryKey[3] === "timeline") && (leadIds === null || leadIds.has(String(query.queryKey[5]))),
  };
}

/**
 * Il Lead archiviato o eliminato non ha più una scheda (404): la sua scheda e la sua Timeline, in qualunque vista, non
 * restano in cache, così «Indietro» non le mostra come se ci fossero ancora. Si chiama dopo aver lasciato la scheda:
 * una scheda ancora aperta la rileggerebbe subito.
 */
export function forgetLead(queryClient: QueryClient, tab: TabContext, leadId: string): void {
  queryClient.removeQueries(leadCardsFilter(tabKey(tab), new Set([leadId])));
}

/**
 * «Archivia» dalla scheda (`POST /api/leads/:leadId/archive`, 204). Il Lead esce subito dalle pagine dell'elenco in
 * cache, così tornando all'elenco non si rivede nemmeno per un attimo, e le letture che lo contavano si rileggono.
 * La scheda e la Timeline le toglie `forgetLead`, dopo aver lasciato la scheda.
 */
export function useArchiveLead(tab: TabContext, leadId: string) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  return useMutation({
    mutationFn: (body: ArchiveLeadBody) => apiRequest("POST", `/api/leads/${encodeURIComponent(leadId)}/archive`, { schema: z.undefined(), body, tab }),
    onSuccess: () => {
      queryClient.setQueriesData<LeadListResponse>({ queryKey: ["scheda", tabPart, "leads", "list"] }, (page) => {
        if (!page || !page.rows.some((row) => row.id === leadId)) return page;
        return { ...page, rows: page.rows.filter((row) => row.id !== leadId), total: Math.max(0, page.total - 1) };
      });
      refreshAfterArchiveChange(queryClient, tab);
    },
  });
}

/** L'esito di un'azione su più archiviati: quelli riusciti e quelli rifiutati, ognuno con il suo rifiuto. */
export type ArchiveBatchResult = { done: ArchivedLeadRow[]; failed: Array<{ row: ArchivedLeadRow; error: unknown }> };

/**
 * I rifiuti che valgono per tutta l'azione e non per un Lead solo (la rete, la sessione, la funzione tolta, la Vista
 * Commerciale chiusa): dopo il primo le richieste che restano non partono, e quei Lead risultano non cambiati con lo stesso
 * motivo, così l'esito li nomina tutti (decisione P36). Le letture rilette subito dopo li gestiscono come ovunque
 * (`handleQueryError`).
 */
const STOPPING_CODES: ReadonlySet<string> = new Set(["network", "unauthenticated", "permission_denied", "commercial_view_denied"]);

/** Una richiesta per Lead, una dopo l'altra: il rifiuto di un Lead non ferma gli altri; uno dell'azione intera sì. */
async function eachRow(rows: readonly ArchivedLeadRow[], send: (row: ArchivedLeadRow) => Promise<unknown>): Promise<ArchiveBatchResult> {
  const result: ArchiveBatchResult = { done: [], failed: [] };
  let stoppedBy: unknown = null;
  for (const row of rows) {
    if (stoppedBy !== null) {
      result.failed.push({ row, error: stoppedBy });
      continue;
    }
    try {
      await send(row);
      result.done.push(row);
    } catch (error) {
      result.failed.push({ row, error });
      if (error instanceof ApiRequestError && STOPPING_CODES.has(error.code)) stoppedBy = error;
    }
  }
  return result;
}

/**
 * «Ripristina» e «Ripristina i selezionati» (`POST /api/leads/:leadId/restore` con la versione letta, 200 con il Lead).
 * Finita l'azione, elenchi, Smart List e archiviati si rileggono una volta sola.
 */
export function useRestoreLead(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rows: readonly ArchivedLeadRow[]) =>
      eachRow(rows, (row) => apiRequest("POST", `/api/leads/${encodeURIComponent(row.id)}/restore`, { schema: leadWriteResponseSchema, body: { version: row.version }, tab })),
    onSettled: () => refreshAfterArchiveChange(queryClient, tab),
  });
}

/**
 * «Elimina per sempre i selezionati» (`DELETE /api/leads/:leadId` con la parola scritta). La richiesta non porta la
 * versione: chi conferma lo fa sulla finestra appena riletta (`ArchivedDialog`). Gli archiviati si rileggono; il Lead
 * eliminato non lascia nulla in cache.
 */
export function useDeleteLead(tab: TabContext) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  const body: DeleteLeadBody = { confirmation: "elimina" };
  return useMutation({
    mutationFn: (rows: readonly ArchivedLeadRow[]) =>
      eachRow(rows, (row) => apiRequest("DELETE", `/api/leads/${encodeURIComponent(row.id)}`, { schema: deleteLeadResponseSchema, body, tab })),
    onSettled: (result) => {
      for (const row of result?.done ?? []) forgetLead(queryClient, tab, row.id);
      for (const queryKey of [archivedKeys.all(tabPart), filterOptionsPrefix(tabPart)]) void queryClient.invalidateQueries({ queryKey });
    },
  });
}

/**
 * Il numero di un Lead archiviato nel rifiuto `409 phone_in_use` (`details.archived`, D9 e questione 67): il numero
 * normalizzato da cercare fra gli archiviati, oppure `null` se il rifiuto non riguarda un Lead archiviato.
 */
export function archivedPhoneOf(error: unknown): string | null {
  if (!(error instanceof ApiRequestError) || error.code !== "phone_in_use" || error.details?.archived !== true) return null;
  const e164 = error.details.e164;
  return typeof e164 === "string" && e164 !== "" ? e164 : null;
}
